import { Search } from 'lucide-react'
import type { StatusStep } from '../utils/statusUtils'
import { statusDotColor } from '../utils/statusUtils'
import type { JournalFilters } from '../utils/journalFilters'

interface JournalFilterBarProps {
  filters: JournalFilters
  onChange: (next: JournalFilters) => void
  statuses: StatusStep[]
}

export default function JournalFilterBar({ filters, onChange, statuses }: JournalFilterBarProps) {
  const set = (patch: Partial<JournalFilters>) => onChange({ ...filters, ...patch })

  const toggleStatus = (id: string) =>
    set({
      statuses: filters.statuses.includes(id)
        ? filters.statuses.filter(s => s !== id)
        : [...filters.statuses, id],
    })

  return (
    <div className="view-toolbar" style={{ flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
      <div className="search-input">
        <Search size={13} />
        <input
          value={filters.search}
          onChange={e => set({ search: e.target.value })}
          placeholder="Sök ÄTA…"
        />
      </div>

      {/* Status chips */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {statuses.map(s => {
          const active = filters.statuses.includes(s.id)
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => toggleStatus(s.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, fontSize: 12,
                padding: '3px 9px', borderRadius: 999, cursor: 'pointer',
                border: '.5px solid var(--border)',
                background: active ? 'var(--bg-2)' : 'transparent',
                color: active ? 'var(--ink)' : 'var(--ink-dim)',
                fontWeight: active ? 600 : 400,
              }}
            >
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: statusDotColor(s) }} />
              {s.kort}
            </button>
          )
        })}
      </div>

      <div style={{ flex: 1 }} />

      {/* Belopp range */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ink-dim)' }}>
        <span>Belopp</span>
        <input
          className="field-input" type="number" inputMode="numeric"
          style={{ width: 90, padding: '3px 7px', fontSize: 12 }}
          value={filters.minBelopp} onChange={e => set({ minBelopp: e.target.value })} placeholder="min"
        />
        <span>–</span>
        <input
          className="field-input" type="number" inputMode="numeric"
          style={{ width: 90, padding: '3px 7px', fontSize: 12 }}
          value={filters.maxBelopp} onChange={e => set({ maxBelopp: e.target.value })} placeholder="max"
        />
      </div>

      {/* Date range (created) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ink-dim)' }}>
        <span>Skapad</span>
        <input
          className="field-input" type="date"
          style={{ padding: '3px 7px', fontSize: 12 }}
          value={filters.fromDate} onChange={e => set({ fromDate: e.target.value })}
        />
        <span>–</span>
        <input
          className="field-input" type="date"
          style={{ padding: '3px 7px', fontSize: 12 }}
          value={filters.toDate} onChange={e => set({ toDate: e.target.value })}
        />
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, color: 'var(--ink-dim)', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={filters.onlyKvar}
          onChange={e => set({ onlyKvar: e.target.checked })}
          style={{ accentColor: 'var(--accent)' }}
        />
        Kvar att fakturera
      </label>
    </div>
  )
}
